import React from 'react';
import './App.css';

const imagePath = (name) => `${process.env.PUBLIC_URL}/images/optimized/${name}.webp`;

const work = [
  {
    id: 'yellow-brick-music',
    name: 'Yellow Brick Music',
    role: 'Director',
    field: 'Music education',
    image: 'yellow-brick-music',
    imageAlt: 'Dylan with the Yellow Brick Music team',
    description: 'Dylan directs Yellow Brick Music, a music school with multiple sites in Waterloo Region.',
    responsibilities: [
      'Oversees finance, staffing, program planning, and daily operations.',
      'Supports staff through hiring, onboarding, and ongoing mentorship.',
      'Introduced self-service enrolment and clearer reporting to replace manual administration.',
    ],
    url: 'https://yellowbrickmusic.ca/',
    linkLabel: 'Visit Yellow Brick Music',
  },
  {
    id: 'fuse',
    name: 'Fuse',
    role: 'Founder & Director · Volunteer',
    field: 'Community arts',
    image: 'fuse',
    imageAlt: 'Dylan at an outdoor Fuse community event',
    description: 'Dylan founded Fuse in 2015. The registered inter-arts charity runs community projects and programming with volunteers and local partners.',
    responsibilities: [
      'Works on strategy, governance, and program planning.',
      'Recruits and mentors volunteers and develops community partnerships.',
      'Manages donor relationships, compliance, and organisational reporting.',
    ],
    url: 'https://www.fuseart.org/',
    linkLabel: 'Visit Fuse',
  },
];

const focusAreas = [
  {
    number: '01',
    title: 'Program development',
    description: 'He develops educational and community programs with instructors, schools, and local partners.',
  },
  {
    number: '02',
    title: 'Teams & operations',
    description: 'His experience includes hiring, onboarding, and mentoring staff, as well as coordinating delivery across multiple sites.',
  },
  {
    number: '03',
    title: 'Reflective practice',
    description: 'He works with staff and volunteers to identify opportunities for growth and improvement, set aside time to address them, and follow through. This includes thoughtfully integrating new technologies and AI to modernize operations.',
  },
];

const photographs = [
  {
    image: 'guitar-lesson',
    alt: 'Dylan playing guitar alongside a young student during a lesson',
    caption: 'Guitar instruction',
  },
  {
    image: 'orchestra-rehearsal',
    alt: 'Dylan conducting musicians in an orchestra rehearsal',
    caption: 'Orchestra rehearsal',
  },
  {
    image: 'community-performance',
    alt: 'Musicians playing guitars and drums at an outdoor Yellow Brick Music performance',
    caption: 'Yellow Brick Music community performance',
  },
];

function Arrow({ diagonal = false }) {
  return <span aria-hidden="true">{diagonal ? '↗' : '→'}</span>;
}

function SectionHeading({ label, title, children, id }) {
  return (
    <div className="section-heading">
      <div>
        <p className="eyebrow">{label}</p>
        <h2 id={id}>{title}</h2>
      </div>
      {children && <p className="section-intro">{children}</p>}
    </div>
  );
}

function WorkCard({ item }) {
  return (
    <article className="work-card" aria-labelledby={`${item.id}-title`}>
      <div className="work-image">
        <img src={imagePath(item.image)} alt={item.imageAlt} width="1000" height={item.image === 'fuse' ? '563' : '822'} loading="lazy" decoding="async" />
        <span className="image-label">{item.field}</span>
      </div>
      <div className="work-content">
        <p className="role-label">{item.role}</p>
        <h3 id={`${item.id}-title`}>{item.name}</h3>
        <p>{item.description}</p>
        <ul className="responsibility-list">
          {item.responsibilities.map((responsibility) => <li key={responsibility}>{responsibility}</li>)}
        </ul>
        <a className="text-link" href={item.url}>{item.linkLabel}<Arrow diagonal /></a>
      </div>
    </article>
  );
}

function Home() {
  return (
    <div className="site">
      <a className="skip-link" href="#content">Skip to content</a>
      <header className="site-header">
        <div className="container header-inner">
          <a className="wordmark" href="#top" aria-label="Dylan Langan, back to top">Dylan Langan<span aria-hidden="true">.</span></a>
          <nav aria-label="Main navigation">
            <a href="#current-work">Work</a>
            <a href="#approach">Approach</a>
            <a href="#background">Background</a>
          </nav>
        </div>
      </header>

      <main id="content" tabIndex="-1">
        <section className="hero container" id="top" aria-labelledby="hero-title">
          <div className="hero-copy">
            <p className="eyebrow"><span className="small-rule" aria-hidden="true" />Waterloo Region, Ontario</p>
            <h1 id="hero-title">Dylan<br />Langan<span className="name-period">.</span></h1>
            <p className="hero-lead">Leadership in education and community organisations</p>
            <p className="hero-description">Dylan Langan has more than a decade of experience leading teams and developing programs. He directs Yellow Brick Music and volunteers as Director of Fuse, with responsibilities ranging from staff support and program development to finance and daily operations.</p>
            <a className="button-link" href="#current-work">View current roles<Arrow /></a>
          </div>
          <figure className="hero-portrait">
            <div className="portrait-frame">
              <img src={imagePath('portrait')} alt="Portrait of Dylan Langan" width="1000" height="1001" fetchpriority="high" />
            </div>
            <figcaption><span>Dylan Langan</span><span className="portrait-caption-line" aria-hidden="true" /></figcaption>
          </figure>
        </section>

        <div className="profile-facts container" aria-label="At a glance">
          <div><strong>10+ years</strong><span>Education & community leadership</span></div>
          <div><strong>Waterloo Region</strong><span>Ontario, Canada</span></div>
          <div><strong>English & French</strong><span>Languages spoken</span></div>
        </div>

        <section id="current-work" className="section container" aria-labelledby="work-heading">
          <SectionHeading id="work-heading" label="Professional experience" title="Current roles">Dylan leads Yellow Brick Music and serves as Fuse’s founder and volunteer director. Across both organizations, his responsibilities span strategy, team development, community partnerships, finance, and daily operations.</SectionHeading>
          <div className="work-grid">{work.map((item) => <WorkCard key={item.id} item={item} />)}</div>
        </section>

        <section id="approach" className="approach-section" aria-labelledby="approach-heading">
          <div className="container section">
            <SectionHeading id="approach-heading" label="Approach" title="How he works">Dylan works alongside staff and volunteers to build a welcoming culture of learning and growth, with time for reflection and practical improvements to programs and daily operations.</SectionHeading>
            <div className="focus-grid">
              {focusAreas.map((area) => (
                <article className="focus-area" key={area.number}>
                  <span className="focus-number" aria-hidden="true">{area.number}</span>
                  <h3>{area.title}</h3>
                  <p>{area.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="background" className="section container background-section" aria-labelledby="background-heading">
          <div className="background-intro">
            <p className="eyebrow">Background</p>
            <h2 id="background-heading">About Dylan</h2>
            <p>Dylan’s background in music has shaped a career in teaching, program development, and organisational leadership. He has led youth orchestras, worked with schools and community groups, and introduced software to simplify administrative work.</p>
            <div className="personal-note">
              <span className="small-rule" aria-hidden="true" />
              <p>He sings and plays piano, guitar, and drums. He has also volunteered as a dog foster with Save Our Scruff.</p>
            </div>
          </div>
          <div className="background-details">
            <div className="background-group">
              <h3>Education</h3>
              <ul className="credential-list">
                <li><strong>Master of Arts</strong><span>Wilfrid Laurier University</span></li>
                <li><strong>Honours Bachelor of Music</strong><span>Wilfrid Laurier University</span></li>
                <li><strong>Computer Science · Mobile Computing</strong><span>Carleton University</span></li>
              </ul>
            </div>
            <div className="background-group">
              <h3>Previous leadership</h3>
              <p className="credential-title">Guelph Symphony Orchestra</p>
              <p>Youth Orchestra Director; Assistant Music Director</p>
            </div>
            <div className="background-group">
              <h3>Recognition</h3>
              <ul className="recognition-list">
                <li>Ontario Medal for Young Volunteers</li>
                <li>Bernice Adams Memorial Award</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="photo-section container" aria-labelledby="photos-heading">
          <div className="photo-heading"><p className="eyebrow">Photos</p><h2 id="photos-heading">Teaching and community programs</h2></div>
          <div className="photo-grid">
            {photographs.map((photo) => (
              <figure key={photo.image}>
                <div className="photo-frame"><img src={imagePath(photo.image)} alt={photo.alt} width="720" height={photo.image === 'orchestra-rehearsal' ? '480' : photo.image === 'community-performance' ? '592' : '540'} loading="lazy" decoding="async" /></div>
                <figcaption>{photo.caption}</figcaption>
              </figure>
            ))}
          </div>
        </section>
      </main>

      <footer className="site-footer">
        <div className="container footer-inner">
          <div><span className="footer-name">Dylan Langan</span><span className="footer-location">Waterloo Region, Ontario</span></div>
          <div className="footer-end"><span>© {new Date().getFullYear()} Dylan Langan</span><a className="text-link" href="#top">Back to top<span aria-hidden="true">↑</span></a></div>
        </div>
      </footer>
    </div>
  );
}

export default Home;
